import { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Folder, Image, Music, Video, FileText, Layers } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

export default function AssetLibrary({ className = '' }: { className?: string }) {
  const sectionRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const screenshotRef = useRef<HTMLDivElement>(null);
  const categoriesRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const ctx = gsap.context(() => {
      const scrollTl = gsap.timeline({
        scrollTrigger: { trigger: section, start: 'top top', end: '+=70%', pin: true, scrub: 0.6 },
      });
      scrollTl.fromTo(contentRef.current, { x: '-20vw', opacity: 0 }, { x: 0, opacity: 1, ease: 'none' }, 0);
      scrollTl.fromTo(screenshotRef.current, { x: '30vw', opacity: 0 }, { x: 0, opacity: 1, ease: 'none' }, 0.05);
      scrollTl.fromTo(categoriesRef.current?.children || [], { y: 20, opacity: 0 }, { y: 0, opacity: 1, stagger: 0.02, ease: 'none' }, 0.1);
      scrollTl.fromTo(contentRef.current, { x: 0, opacity: 1 }, { x: '-10vw', opacity: 0, ease: 'power2.in' }, 0.7);
      scrollTl.fromTo(screenshotRef.current, { x: 0, opacity: 1 }, { x: '10vw', opacity: 0, ease: 'power2.in' }, 0.72);
    }, section);

    return () => ctx.revert();
  }, []);

  const categories = [
    { icon: Image, label: 'Logos', count: '24' },
    { icon: Layers, label: 'Headshots', count: '18' },
    { icon: Folder, label: 'Flyers', count: '56' },
    { icon: FileText, label: 'Backgrounds', count: '32' },
    { icon: Video, label: 'Textures', count: '12' },
    { icon: Music, label: 'Generated', count: '89' },
  ];

  return (
    <section ref={sectionRef} className={`pinned-section bg-[#0B0F10] flex items-center ${className}`}>
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div ref={contentRef} style={{ willChange: 'transform, opacity' }}>
            <span className="font-mono text-xs uppercase tracking-[0.12em] text-[#2EC4B6] mb-4 block">Asset Library</span>
            <h2 className="font-display font-bold text-[clamp(36px,4.5vw,64px)] leading-tight text-white mb-6">
              Everything in<br />
              <span className="text-[#2EC4B6]">one place.</span>
            </h2>
            <p className="text-lg text-[#A9B4B8] leading-relaxed mb-8">
              Store all your logos, headshots, backgrounds, and brand assets. Organized by artist, venue, and event—so you never hunt for files again.
            </p>
            <div ref={categoriesRef} className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {categories.map((cat, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3 bg-white/5 rounded-lg border border-white/10 hover:border-[#2EC4B6]/30 transition-colors">
                  <cat.icon size={18} className="text-[#2EC4B6]" />
                  <div>
                    <div className="text-sm text-white">{cat.label}</div>
                    <div className="text-xs text-[#A9B4B8]">{cat.count} items</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div ref={screenshotRef} className="relative" style={{ willChange: 'transform, opacity' }}>
            <div className="relative rounded-xl overflow-hidden border border-white/10 shadow-2xl">
              <div className="absolute -inset-1 bg-gradient-to-r from-[#2EC4B6]/10 to-transparent blur-xl opacity-50" />
              <img src="/screenshot-library.png" alt="Asset Library" className="relative w-full rounded-xl" />
            </div>
            <div className="absolute -bottom-4 -left-4 bg-[#0B0F10] border border-[#2EC4B6]/30 rounded-lg px-4 py-3 shadow-xl">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#2EC4B6] animate-pulse" />
                <span className="text-sm text-white">11 assets ready</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
